import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWindowSize } from '../hooks/useWindowSize';
import Toast from '../components/Toast';
import { C, E, RAYON, T } from '../theme';
import { API } from '../api';
import { IconeCorbeille, IconeCrayon } from '../components/Icones';

// Repères communs à cette page (voir src/theme.js).
const carteSection = {
  background: C.surface, borderRadius: RAYON.lg, padding: E.lg,
  border: `1px solid ${C.bordure}`,
};
const titreSection = { fontSize: '14px', fontWeight: 600, color: C.texte,
                       margin: `0 0 ${E.lg}` };

const TYPE_COLORS = {
  RDV:     { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe', emoji: '📅' },
  Relance: { bg: '#fef3c7', text: '#92400e', border: '#fde68a', emoji: '🔔' },
  Appel:   { bg: '#d1fae5', text: '#065f46', border: '#a7f3d0', emoji: '📞' },
  // Créées depuis la fiche entreprise, attribuées à quelqu'un, clôturées par
  // un admin seul. Sans cette entrée, elles s'afficheraient aux couleurs des
  // rendez-vous — le même piège que les libellés de contrat côté client.
  'Tâche': { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe', emoji: '✅' },
};

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const today = new Date();

function Agenda() {
  const { isMobile, isTablet } = useWindowSize();
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [mois, setMois] = useState(today.getMonth() + 1);
  const [annee, setAnnee] = useState(today.getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ titre: '', type: 'RDV', date_debut: '', prospect_id: '', note: '' });
  const [editingEventId, setEditingEventId] = useState(null);
  const [draggingEventId, setDraggingEventId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [showPasses, setShowPasses] = useState(false);

  const fetchEvents = useCallback(() => {
    axios.get(`${API}/agenda?mois=${mois}&annee=${annee}`)
      .then(res => setEvents(res.data))
      .catch(() => {});
  }, [mois, annee]);

  const fetchAllEvents = useCallback(() => {
    axios.get(`${API}/agenda`)
      .then(res => setAllEvents(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchAllEvents(); }, [fetchAllEvents]);
  useEffect(() => {
    axios.get(`${API}/prospects`).then(res => setProspects(res.data)).catch(() => {});
  }, []);

  // ── Grille mensuelle ────────────────────────────────────────
  const premierJour = new Date(annee, mois - 1, 1);
  const dernierJour = new Date(annee, mois, 0);
  const offsetDebut = (premierJour.getDay() + 6) % 7;

  const cases = [];
  for (let i = 0; i < offsetDebut; i++) cases.push(null);
  for (let d = 1; d <= dernierJour.getDate(); d++) cases.push(d);
  while (cases.length % 7 !== 0) cases.push(null);

  const eventsParJour = {};
  events.forEach(e => {
    const d = new Date(e.date_debut);
    if (d.getMonth() + 1 === mois && d.getFullYear() === annee) {
      const key = d.getDate();
      if (!eventsParJour[key]) eventsParJour[key] = [];
      eventsParJour[key].push(e);
    }
  });

  // Convertit un Date UTC en chaîne locale pour datetime-local input
  const toLocalInputStr = (dt) => {
    const offset = dt.getTimezoneOffset() * 60000;
    return new Date(dt.getTime() - offset).toISOString().slice(0, 16);
  };

  const ouvrirFormJour = (jour) => {
    const dt = new Date(annee, mois - 1, jour, 9, 0);
    setForm({ titre: '', type: 'RDV', date_debut: toLocalInputStr(dt), prospect_id: '', note: '' });
    setSelectedDay(jour);
    setEditingEventId(null);
    setShowForm(true);
  };

  const ouvrirEditionEvent = (ev, event) => {
    ev.stopPropagation();
    setForm({
      titre: event.titre,
      type: event.type,
      date_debut: toLocalInputStr(new Date(event.date_debut)),
      prospect_id: event.prospect_id || '',
      note: event.note || '',
    });
    setEditingEventId(event.id);
    setSelectedDay(null);
    setShowForm(true);
  };

  const fermerForm = () => {
    setShowForm(false);
    setSelectedDay(null);
    setEditingEventId(null);
  };

  const buildPayload = () => ({
    titre: form.titre,
    type: form.type,
    date_debut: new Date(form.date_debut).toISOString(),
    prospect_id: form.prospect_id ? parseInt(form.prospect_id) : null,
    note: form.note || null,
  });

  const creerEvent = () => {
    if (!form.titre.trim() || !form.date_debut) return;
    axios.post(`${API}/agenda`, buildPayload())
      .then(() => { fetchEvents(); fetchAllEvents(); fermerForm(); setToast({ message: 'Événement planifié !', type: 'success' }); });
  };

  const sauvegarderEdit = () => {
    if (!form.titre.trim() || !form.date_debut) return;
    axios.put(`${API}/agenda/${editingEventId}`, buildPayload())
      .then(() => { fetchEvents(); fetchAllEvents(); fermerForm(); setToast({ message: 'Événement modifié !', type: 'success' }); });
  };

  const supprimerEvent = (id) => {
    axios.delete(`${API}/agenda/${id}`)
      .then(() => {
        setEvents(events.filter(e => e.id !== id));
        setAllEvents(allEvents.filter(e => e.id !== id));
        setToast({ message: 'Événement supprimé.', type: 'success' });
      });
  };

  const deplacerEvent = (eventId, newDay) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const oldDate = new Date(event.date_debut);
    const newDate = new Date(annee, mois - 1, newDay, oldDate.getHours(), oldDate.getMinutes());
    // Mise à jour optimiste
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, date_debut: newDate.toISOString() } : e));
    axios.patch(`${API}/agenda/${eventId}/date`, { date_debut: newDate.toISOString() })
      .catch(() => fetchEvents());
    setDraggingEventId(null);
    setToast({ message: 'Événement déplacé !', type: 'success' });
  };

  const naviguerMois = (delta) => {
    let m = mois + delta, a = annee;
    if (m < 1) { m = 12; a--; }
    if (m > 12) { m = 1; a++; }
    setMois(m); setAnnee(a);
  };

  const estAujourdHui = (jour) =>
    jour === today.getDate() && mois === today.getMonth() + 1 && annee === today.getFullYear();

  const now = new Date();
  const eventsAVenir = allEvents
    .filter(e => new Date(e.date_debut) >= now)
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
  const eventsPasses = allEvents
    .filter(e => new Date(e.date_debut) < now)
    .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));

  const modalTitle = editingEventId
    ? "Modifier l'événement"
    : selectedDay
      ? `Planifier le ${selectedDay} ${MOIS_FR[mois - 1]}`
      : 'Planifier un événement';

  return (
    <div style={{ fontFamily: T.police }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={T.titre}>Agenda</h1>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>RDV, relances et appels planifiés</p>
        </div>
        <button
          onClick={() => { setForm({ titre: '', type: 'RDV', date_debut: '', prospect_id: '', note: '' }); setEditingEventId(null); setSelectedDay(null); setShowForm(true); }}
          style={{ background: '#1a2b4a', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
          + Nouvel événement
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '20px' }}>

        {/* Calendrier */}
        <div style={carteSection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <button onClick={() => naviguerMois(-1)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px' }}>←</button>
            <h2 style={{ color: '#1a2b4a', fontSize: '18px', fontWeight: '600', margin: 0 }}>{MOIS_FR[mois - 1]} {annee}</h2>
            <button onClick={() => naviguerMois(1)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px' }}>→</button>
          </div>

          <div style={{ overflowX: isMobile || isTablet ? 'auto' : 'visible' }}>
          <div style={{ minWidth: isMobile || isTablet ? '560px' : 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
            {JOURS.map(j => (
              <div key={j} style={{ textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0' }}>{j}</div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {cases.map((jour, i) => (
              <div key={i}
                onClick={() => jour && !draggingEventId && ouvrirFormJour(jour)}
                onDragOver={(ev) => { if (jour && draggingEventId) { ev.preventDefault(); setDragOverDay(jour); } }}
                onDragLeave={(ev) => { if (jour && !ev.currentTarget.contains(ev.relatedTarget)) setDragOverDay(null); }}
                onDrop={(ev) => { ev.preventDefault(); if (jour && draggingEventId) { deplacerEvent(draggingEventId, jour); setDragOverDay(null); } }}
                style={{
                  minHeight: '72px', borderRadius: '8px', padding: '6px',
                  background: estAujourdHui(jour) ? '#1a2b4a' : dragOverDay === jour ? '#eff6ff' : jour ? '#fafafa' : 'transparent',
                  border: jour ? (dragOverDay === jour ? '2px dashed #3b82f6' : '1px solid #f3f4f6') : 'none',
                  cursor: jour ? 'pointer' : 'default',
                  opacity: jour ? 1 : 0,
                  transition: 'background 0.12s, border-color 0.12s',
                  boxSizing: 'border-box',
                }}>
                {jour && (
                  <>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: estAujourdHui(jour) ? 'white' : '#374151', marginBottom: '4px' }}>{jour}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {(eventsParJour[jour] || []).slice(0, 3).map(e => {
                        const tc = TYPE_COLORS[e.type] || TYPE_COLORS.RDV;
                        return (
                          <div key={e.id} title={`${e.titre} — cliquer pour voir le détail`}
                            draggable
                            onDragStart={(ev) => { ev.stopPropagation(); setDraggingEventId(e.id); }}
                            onDragEnd={() => { setDraggingEventId(null); setDragOverDay(null); }}
                            onClick={(ev) => { ev.stopPropagation(); setDetailEvent(e); }}
                            style={{
                              background: tc.bg, color: tc.text, fontSize: '10px', fontWeight: '500',
                              padding: '1px 5px', borderRadius: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              opacity: draggingEventId === e.id ? 0.35 : 1,
                              cursor: 'grab',
                              transition: 'opacity 0.15s',
                            }}>
                            {tc.emoji} {e.titre}
                          </div>
                        );
                      })}
                      {(eventsParJour[jour] || []).length > 3 && (
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>+{(eventsParJour[jour] || []).length - 3}</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: '12px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
            Glissez un événement pour le déplacer · Cliquez dessus pour le modifier
          </div>
          </div>{/* fin minWidth */}
          </div>{/* fin overflowX */}
        </div>

        {/* Sidebar : événements */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 160px)', minHeight: '200px' }}>
          {/* Titre + compteur */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexShrink: 0 }}>
            <h3 style={{ color: '#1a2b4a', fontSize: '14px', fontWeight: '600', margin: 0 }}>
              {showPasses ? 'RDV passés' : 'Prochains RDV'}
            </h3>
            <span style={{ fontSize: '12px', fontWeight: '500', color: '#9ca3af', background: '#f3f4f6', borderRadius: '99px', padding: '2px 8px' }}>
              {showPasses ? eventsPasses.length : eventsAVenir.length}
            </span>
          </div>

          {/* Toggle À venir / Passés */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', flexShrink: 0, background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
            <button onClick={() => setShowPasses(false)} style={{
              flex: 1, padding: '5px 0', borderRadius: '6px', border: 'none',
              background: !showPasses ? 'white' : 'transparent',
              color: !showPasses ? '#1a2b4a' : '#9ca3af',
              cursor: 'pointer', fontSize: '12px', fontWeight: !showPasses ? '600' : '400',
              boxShadow: !showPasses ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>À venir</button>
            <button onClick={() => setShowPasses(true)} style={{
              flex: 1, padding: '5px 0', borderRadius: '6px', border: 'none',
              background: showPasses ? 'white' : 'transparent',
              color: showPasses ? '#1a2b4a' : '#9ca3af',
              cursor: 'pointer', fontSize: '12px', fontWeight: showPasses ? '600' : '400',
              boxShadow: showPasses ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>Historique</button>
          </div>

          {/* Liste */}
          {(() => {
            const liste = showPasses ? eventsPasses : eventsAVenir;
            if (liste.length === 0) return (
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
                {showPasses ? 'Aucun RDV passé.' : 'Aucun événement à venir.'}
              </p>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', paddingRight: '4px' }}>
                {liste.map(e => {
                  const tc = TYPE_COLORS[e.type] || TYPE_COLORS.RDV;
                  const d = new Date(e.date_debut);
                  const prospect = prospects.find(p => p.id === e.prospect_id);
                  const passe = showPasses;
                  return (
                    <div key={e.id} onClick={() => setDetailEvent(e)}
                      style={{ border: `1px solid ${passe ? '#e5e7eb' : tc.border}`, borderRadius: '8px', padding: '10px 12px', background: passe ? '#f9fafb' : tc.bg, cursor: 'pointer', opacity: passe ? 0.85 : 1, transition: 'opacity 0.1s' }}
                      onMouseEnter={el => el.currentTarget.style.opacity = '1'}
                      onMouseLeave={el => el.currentTarget.style.opacity = passe ? '0.85' : '1'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: passe ? '#6b7280' : tc.text }}>{tc.emoji} {e.titre}</div>
                          <div style={{ fontSize: '11px', color: passe ? '#9ca3af' : tc.text, opacity: passe ? 1 : 0.8, marginTop: '2px' }}>
                            {d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} à {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {prospect && <div style={{ fontSize: '11px', color: passe ? '#9ca3af' : tc.text, opacity: 0.7, marginTop: '2px' }}>{prospect.nom_societe}</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', paddingLeft: '6px' }} onClick={ev => ev.stopPropagation()}>
                          {!passe && (
                            <button onClick={(ev) => ouvrirEditionEvent(ev, e)} title="Modifier"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: tc.text, opacity: 0.6, fontSize: '12px', padding: '2px', lineHeight: 1 }}><IconeCrayon taille={15} /></button>
                          )}
                          <button onClick={() => supprimerEvent(e.id)} title="Supprimer"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: passe ? '#9ca3af' : tc.text, opacity: 0.5, fontSize: '15px', padding: '2px', lineHeight: 1 }}>×</button>
                        </div>
                      </div>
                      {e.note && <div style={{ fontSize: '11px', color: passe ? '#9ca3af' : tc.text, opacity: 0.7, marginTop: '6px', borderTop: `1px solid ${passe ? '#e5e7eb' : tc.border}`, paddingTop: '6px' }}>{e.note}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Modal détail événement */}
      {detailEvent && (() => {
        const ev = detailEvent;
        const tc = TYPE_COLORS[ev.type] || TYPE_COLORS.RDV;
        const d = new Date(ev.date_debut);
        const prospect = prospects.find(p => p.id === ev.prospect_id);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
                <div>
                  <div style={{ display: 'inline-block', background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
                    {tc.emoji} {ev.type}
                  </div>
                  <h2 style={{ color: '#1a2b4a', fontSize: '18px', fontWeight: '700', margin: 0 }}>{ev.titre}</h2>
                </div>
                <button onClick={() => setDetailEvent(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 0 0 12px' }}>×</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px' }}>📅</span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      {d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      à {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                {prospect && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '16px' }}>📌</span>
                    <div style={{ fontSize: '14px', color: '#1a2b4a', fontWeight: '500' }}>{prospect.nom_societe}</div>
                  </div>
                )}
                {ev.note && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '16px', marginTop: '1px' }}>📝</span>
                    <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', flex: 1, background: '#f8fafc', borderRadius: '6px', padding: '8px 10px' }}>{ev.note}</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { setDetailEvent(null); ouvrirEditionEvent({ stopPropagation: () => {} }, ev); }}
                  style={{ flex: 1, background: '#1a2b4a', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Modifier
                </button>
                <button title="Supprimer" onClick={() => { supprimerEvent(ev.id); setDetailEvent(null); }}
                  style={{ background: 'white', color: '#ef4444', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}><IconeCorbeille taille={15} />
                </button>
                <button onClick={() => setDetailEvent(null)}
                  style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal création / édition */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={titreSection}>{modalTitle}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>Type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['RDV', 'Relance', 'Appel'].map(t => {
                    const tc = TYPE_COLORS[t];
                    return (
                      <button key={t} onClick={() => setForm({ ...form, type: t })} style={{
                        padding: '7px 16px', borderRadius: '6px', border: `1px solid ${form.type === t ? tc.border : '#e5e7eb'}`,
                        background: form.type === t ? tc.bg : 'white', color: form.type === t ? tc.text : '#6b7280',
                        cursor: 'pointer', fontSize: '13px', fontWeight: form.type === t ? '600' : '400',
                      }}>{tc.emoji} {t}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>Titre *</label>
                <input type="text" value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })}
                  placeholder="Ex : Appel de suivi"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>Date et heure *</label>
                <input type="datetime-local" value={form.date_debut} onChange={e => setForm({ ...form, date_debut: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>Entreprise (optionnel)</label>
                <select value={form.prospect_id} onChange={e => setForm({ ...form, prospect_id: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', background: 'white', boxSizing: 'border-box' }}>
                  <option value="">— Aucune —</option>
                  {prospects.map(p => <option key={p.id} value={p.id}>{p.nom_societe}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                  rows={2} placeholder="Note optionnelle..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={editingEventId ? sauvegarderEdit : creerEvent}
                style={{ flex: 1, background: '#1a2b4a', color: 'white', padding: '11px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                {editingEventId ? '✅ Sauvegarder' : 'Planifier'}
              </button>
              <button onClick={fermerForm}
                style={{ padding: '11px 20px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default Agenda;
